import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { GoogleService } from '../../../services/google.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environmentConfig } from '../../../../environment';
import { Modal } from 'bootstrap';

export interface GoogleDriveSelectionPayload {
  folderId: string | null;
  folderName: string | null;
  driveId: string | null;
  driveName: string | null;
  isShared: boolean;
  breadcrumbs: { id: string | null; name: string }[];
}

@Component({
  selector: 'app-google-drive-access',
  templateUrl: './google-drive-access.component.html',
  styleUrls: ['./google-drive-access.component.css']
})
export class GoogleDriveAccessComponent implements OnInit {
  drives: any[] = [];
  items: any[] = [];
  folders: any[] = [];
  currentDrive: any = null;
  currentFolderId: string | null = null;
  loading = false;
  breadcrumbs: { id: string | null; name: string }[] = [];
  selectedDriveId = "";
  apiUrl = environmentConfig.apiBaseUrl;
  newFolderName = "";
  editorMode = "Create Folder";
  editorIcon = "folder";
  errorMsg = "";
  errorMsgColor = "red";
  fileContainer: any;
  uploadFiles: File[] | null = null;
  uploadList: any[] = [];
  uploadDetail = "";

  // NEW: latest selection payload (derived from last breadcrumb)
  selection: GoogleDriveSelectionPayload | null = null;

  // NEW: emit selection so parent pages can reuse this component
  @Output() folderChanged = new EventEmitter<GoogleDriveSelectionPayload>();

  constructor(
    private googleService: GoogleService,
    private http: HttpClient
  ) { }

  async ngOnInit() {
    await this.loadSharedDrives();
    await this.openMyDrive();
  }

  private updateSelectionAndEmit() {
    const last = this.breadcrumbs?.length ? this.breadcrumbs[this.breadcrumbs.length - 1] : null;

    const payload: GoogleDriveSelectionPayload = {
      folderId: last?.id ?? null,
      folderName: last?.name ?? null,
      driveId: this.currentDrive?.isShared ? (this.currentDrive?.id ?? null) : null,
      driveName: this.currentDrive?.isShared ? (this.currentDrive?.name ?? null) : null,
      isShared: !!this.currentDrive?.isShared,
      breadcrumbs: [...(this.breadcrumbs || [])]
    };

    this.selection = payload;
    this.folderChanged.emit(payload);
  }

  // LOAD MY DRIVE
  async openMyDrive() {
    this.loading = true;
    try {
      this.selectedDriveId = '';

      const root = await firstValueFrom(this.googleService.getUserDriveRoot());
      this.currentDrive = { id: 'root', name: 'My Drive', isShared: false };
      this.breadcrumbs = [{ id: root.id, name: root.name }];
      await this.loadFolderContents(root.id, false);
    } finally {
      this.loading = false;
    }
  }

  // LOAD SHARED DRIVES LIST
  async loadSharedDrives() {
    this.loading = true;
    try {
      this.drives = await firstValueFrom(this.googleService.getUserSharedDrives());
    } catch (err) {
      console.error('Error loading shared drives', err);
    } finally {
      this.loading = false;
    }
  }

  // OPEN SHARED DRIVE ROOT
  async openDrive(drive: any) {
    this.selectedDriveId = drive.id;

    this.currentDrive = { ...drive, isShared: true };
    this.breadcrumbs = [{ id: drive.id, name: drive.name }];
    await this.loadFolderContents(drive.id, true);
  }

  private async refreshCurrentFolder(delayMs = 300) {
    const last = this.breadcrumbs?.length ? this.breadcrumbs[this.breadcrumbs.length - 1] : null;
    const folderId = last?.id ?? this.currentFolderId ?? 'root';
    const isShared = !!this.currentDrive?.isShared;

    if (delayMs > 0) 
      await new Promise(resolve => setTimeout(resolve, delayMs));

    await this.loadFolderContents(folderId, isShared);
  }

  // LOAD FILES / FOLDERS
  async loadFolderContents(folderId: string | null, isShared: boolean) {
    this.loading = true;
    try {
      let result;
      if (isShared && this.currentDrive?.id) {
        result = await firstValueFrom(
          this.googleService.getUserSharedDriveFiles(this.currentDrive.id, folderId || this.currentDrive.id)
        );
      } else {
        result = await firstValueFrom(this.googleService.getUserDriveFiles(folderId || 'root'));
      }
      this.items = result.files.filter((x:any) => x.mimeType.indexOf('.folder') === -1) || [];
      this.folders = result.files.filter((x: any) => x.mimeType.indexOf('.folder') > -1) || [];
      this.sortByName();



      console.log("Folder Items", this.items,this.folders)

      this.currentFolderId = folderId;

      // NEW: keep selection in sync with navigation
      this.updateSelectionAndEmit();
    } catch (err) {
      console.error('Error loading folder contents', err);
      this.items = [];
      this.updateSelectionAndEmit();
    } finally {
      this.loading = false;
    }
  }

  // OPEN FOLDER
  async openFolder(folder: any) {
    this.breadcrumbs.push({ id: folder.id, name: folder.name });
    await this.loadFolderContents(folder.id, this.currentDrive?.isShared);
  }

  // BREADCRUMB NAVIGATION
  async goToBreadcrumb(index: number) {
    const crumb = this.breadcrumbs[index];
    this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
    await this.loadFolderContents(crumb.id, this.currentDrive?.isShared);
  }

  onFileDoubleClick(item: any) {
    if (item.mimeType !== 'application/vnd.google-apps.folder' && item.webViewLink) {
      window.open(item.webViewLink, '_blank');
    }
  }

  onDriveSelect(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedDriveId = select.value;

    const index = select.selectedIndex - 1;
    if (index >= 0 && this.drives[index]) {
      this.openDrive(this.drives[index]);
    }
  }

  async populateList(event: any): Promise<void> {
    this.fileContainer = (event.target as HTMLInputElement)?.files;
    const fileCounter = (event.target as HTMLInputElement)?.files?.length;

    this.uploadList = [];
    if (fileCounter! > 0)
      for (let f = 0; f < fileCounter!; f++) {
        const selectedFile = (event.target as HTMLInputElement)?.files?.[f] ?? null;
        this.uploadList.push(selectedFile?.name);
      }
    else
      this.uploadList = [];


    // format the file list for display
    this.uploadDetail = this.uploadList.join('<br>');
  }

  async sendSelectionToBackend() {
  if (!this.selection) {
    console.warn('No selection available yet.');
    return;
  }

  const payload = {
    folderId: this.selection.folderId,
    folderName: this.selection.folderName,
    driveId: this.selection.driveId,
    driveName: this.selection.driveName,
    isShared: this.selection.isShared,
    breadcrumbs: this.selection.breadcrumbs,
    fileName: `Test Doc - ${new Date().toISOString()}`
  };

  console.log('Google Drive selection payload:', payload);

  try {
    const res = await firstValueFrom(this.googleService.createDocInFolder(payload));
    console.log('Created doc response:', res);

    await this.refreshCurrentFolder(500);

    //open link to document
    const link = (res as any)?.webViewLink;
    console.log(link)
    //if (link) window.open(link, '_blank');
  } catch (err) {
    console.error('Create doc failed:', err);
  }
  }

  newFolder() {
    this.editorIcon = "folder"
    this.editorMode = "Create Folder";
    this.newFolderName = "";
    $('#googleDriveModal').modal('show');
  }

  uploadFilesToDrive() {
    this.editorIcon = "upload"
    this.editorMode = "Upload Files";
    $('#googleDriveModal').modal('show');
  }


  async createFolder() {
    if (this.newFolderName == "") {
      this.errorMsg = "Please provide a folder name to continue";
      this.clearErrorState();
      $('#new_folder_name').focus();
      return false;
    }

    const url = `${this.apiUrl}/api/google/createfolder?folderName=${this.newFolderName}&parentFolderId=${this.selection!.folderId}`;

    try {
      const test = await this.http.get(url);
      test.toPromise().then(r => {
        this.refreshCurrentFolder(300);
        this.newFolderName = "";
        $('#googleDriveModal').modal('hide');
      })
      return test;
    } catch (ex) {
      return null;
    }
  }

  clearErrorState(length = 5000): void {
    setTimeout(() => {
      this.errorMsg = "";
      this.errorMsgColor = "red";
    }, length)
  }


  sortByName() {
    this.folders.sort((a, b) => {
      // Sort in ascending order by name
      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
      return 0;
    });
  }
}
